import React, { useEffect, useState } from 'react'
import { ORG_PLAN_TIERS, PROJECT, TEAM_SCALE_PLAN } from 'app-constants'
import api from 'helpers/api'
import { capitaliseFirstLetter } from 'helpers/utils'
import { useAuth } from 'hooks/auth'
import useProject from 'hooks/useProject'
import { useHubFailure } from 'hooks/useShownHub'
import { useApplicationStore } from 'hooks/application'
import Badge from 'components/badge'
import Button from 'components/button'
import Dialog from 'components/dialog'
import Toggle from 'components/toggle'
import { BLUE_BUTTON } from 'components/save-buttons'

/**
 * Duplicates the hub, in our dialog: what's copied, whether the copy is locked to this hub (a Team and
 * Scale-Up feature), a warning when the hub has scripts, and Duplicate Hub. Duplication runs in the
 * background; an email says when it's done.
 */
const DuplicateHubDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { project } = useProject()
  const { user } = useAuth()
  const { setSnackbarMessage } = useApplicationStore()
  const hubFailed = useHubFailure(project.publicId)
  const [locked, setLocked] = useState(false)
  const [saving, setSaving] = useState(false)
  const [scriptCount, setScriptCount] = useState(0)
  const onPlan = user?.team?.orgPlanTier === ORG_PLAN_TIERS.TEAM || user?.team?.orgPlanTier === ORG_PLAN_TIERS.SCALE_UP

  useEffect(() => {
    let cancelled = false
    api
      .getHubScriptCount(project.publicId)
      .then((response) => {
        if (!cancelled) setScriptCount(response.data.count)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [project.publicId])

  const duplicate = async () => {
    const hub = project.publicId
    setSaving(true)
    try {
      await api.duplicateProject({ publicId: hub, duplicatePermissions: true, lockResource: locked })
      setSaving(false)
      setSnackbarMessage({
        status: 'success',
        message: `Duplicating ${project.name}. You'll get an email when the new ${PROJECT} is ready.`
      })
      onClose()
    } catch (error) {
      setSaving(false)
      hubFailed(error, hub, `${project.name} couldn't be duplicated. Open its settings to try again.`)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Duplicate ${capitaliseFirstLetter(PROJECT)}`}
      description={`Copies all tables, documents, ${PROJECT} settings, permissions and webhooks into a new ${PROJECT}. You'll get an email when it's ready.`}
      footer={
        <>
          <Button internalType="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button internalType="outline" className={BLUE_BUTTON} isLoading={saving} onClick={duplicate}>
            Duplicate {capitaliseFirstLetter(PROJECT)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex items-center gap-2 text-[13px] font-medium text-body">
              Lock the duplicate
              {!onPlan && <Badge>{TEAM_SCALE_PLAN}</Badge>}
            </span>
            <span className="text-pretty text-xs leading-4 text-muted">
              The structure of the duplicate&apos;s tables and documents cannot be changed. Their data can still be
              entered and edited.
            </span>
          </div>
          <Toggle toggled={locked} onToggle={setLocked} aria-label="Lock the duplicate" />
        </div>
        {scriptCount > 0 && (
          <p className="rounded-lg border border-alert bg-really-light-yellow px-3 py-2.5 text-xs leading-4 text-orange">
            This {PROJECT} has {scriptCount} {scriptCount === 1 ? 'script' : 'scripts'}. They&apos;re copied with the
            duplicate, but scripts stop running after November 2026, when the feature is retired.
          </p>
        )}
      </div>
    </Dialog>
  )
}

export default DuplicateHubDialog
